import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';

type FormTab = 'contact' | 'demo';

export default function ContactPage() {
  const [activeTab, setActiveTab] = useState<FormTab>('demo');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = '联系我们 / 申请DEMO | META66元宇宙科技平台';

    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://meta66.cn';

    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'ContactPage',
        '@id': `${siteUrl}/contact#webpage`,
        url: `${siteUrl}/contact`,
        name: '联系我们 / 申请DEMO | META66元宇宙科技平台',
        description: '联系META66商务团队，申请元宇宙DEMO体验，探索XR沉浸、6G超连接、数字孪生、量子加密等前沿科技合作机会。',
        inLanguage: 'zh-CN',
        dateModified: '2026-04-03',
        isPartOf: { '@id': `${siteUrl}/#website` },
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: siteUrl },
            { '@type': 'ListItem', position: 2, name: '联系我们', item: `${siteUrl}/contact` },
          ],
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        '@id': `${siteUrl}/#organization`,
        name: 'META66',
        url: siteUrl,
        logo: `${siteUrl}/logo.png`,
        contactPoint: [
          {
            '@type': 'ContactPoint',
            telephone: '400-META-666',
            contactType: 'customer service',
            availableLanguage: ['Chinese', 'English'],
            hoursAvailable: {
              '@type': 'OpeningHoursSpecification',
              dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
              opens: '09:00',
              closes: '18:00',
            },
          },
          {
            '@type': 'ContactPoint',
            email: 'bd@meta66.cn',
            contactType: 'sales',
            availableLanguage: ['Chinese', 'English'],
          },
          {
            '@type': 'ContactPoint',
            email: 'support@meta66.cn',
            contactType: 'technical support',
            availableLanguage: ['Chinese', 'English'],
          },
        ],
        address: {
          '@type': 'PostalAddress',
          addressLocality: '深圳市',
          addressRegion: '广东省',
          addressCountry: 'CN',
          streetAddress: '前海合作区META66元宇宙大厦18-22层',
        },
      },
    ];

    const existingScript = document.getElementById('schema-contact-jsonld');
    if (existingScript) existingScript.remove();

    const script = document.createElement('script');
    script.id = 'schema-contact-jsonld';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schemas);
    document.head.appendChild(script);

    return () => {
      const s = document.getElementById('schema-contact-jsonld');
      if (s) s.remove();
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const textarea = form.querySelector('textarea');
    if (textarea && textarea.value.length > 500) {
      alert('留言内容不能超过500字');
      return;
    }
    setSubmitting(true);
    try {
      const formData = new FormData(form);
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        body.append(key, value.toString());
      });
      body.append('form_type', activeTab === 'demo' ? '申请DEMO' : '联系我们');
      await fetch('https://readdy.ai/api/form/d77qasadqn9mib2tqc1g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  const contactInfo = [
    { icon: 'ri-phone-line', label: '商务热线', value: '400-META-666', sub: '周一至周五 9:00-18:00' },
    { icon: 'ri-mail-line', label: '商务邮箱', value: 'bd@meta66.cn', sub: '24小时内回复' },
    { icon: 'ri-customer-service-line', label: '技术支持', value: 'support@meta66.cn', sub: '7×24小时响应' },
    { icon: 'ri-map-pin-line', label: '总部地址', value: '深圳市前海合作区', sub: 'META66元宇宙大厦 18-22层' },
  ];

  const demoFeatures = [
    { icon: 'ri-glasses-line', title: 'XR沉浸体验', desc: '亲身体验MetaLens Pro带来的全息沉浸感' },
    { icon: 'ri-signal-tower-line', title: '6G网络演示', desc: '感受0.1ms超低延迟的元宇宙实时交互' },
    { icon: 'ri-global-line', title: 'Web5.0协议', desc: '了解去中心化身份与数字资产主权' },
    { icon: 'ri-building-2-line', title: '数字孪生城市', desc: '参观雄安数字孪生城市实时演示系统' },
    { icon: 'ri-brain-line', title: '脑机接口体验', desc: '体验MetaMind意念控制元宇宙' },
    { icon: 'ri-shield-keyhole-line', title: '量子加密演示', desc: '了解MetaShield量子安全防护体系' },
  ];

  return (
    <div className="min-h-screen bg-[#020810]" style={{ fontFamily: "'Inter', 'PingFang SC', sans-serif" }}>
      <Navbar />

      {/* Header */}
      <section className="relative pt-28 pb-16 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/c2710046c0577e749e7c9327108f3a52.jpg"
            alt="联系我们背景"
            className="w-full h-full object-cover object-top opacity-15"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#020810]/70 via-[#020810]/50 to-[#020810]" />
        </div>
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: 'linear-gradient(rgba(0,255,136,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(0,255,136,0.03) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        />
        <div className="relative z-10 max-w-[1400px] mx-auto px-4 md:px-10">
          <div className="flex items-center gap-2 text-white/30 text-sm mb-8 flex-wrap">
            <Link to="/" className="hover:text-[#00FF88] transition-colors whitespace-nowrap">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-white/60 whitespace-nowrap">联系我们</span>
          </div>
          <div className="flex items-center gap-3 mb-4">
            <div className="flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#00FF88]/30 bg-[#00FF88]/5">
              <span className="w-2 h-2 rounded-full bg-[#00FF88] animate-pulse" />
              <span className="text-[#00FF88] text-xs font-semibold tracking-widest uppercase">Contact Us</span>
            </div>
          </div>
          <h1 className="text-4xl md:text-5xl font-black text-white mb-4" style={{ fontFamily: 'Orbitron, sans-serif' }}>
            联系我们 /<span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00FF88] to-[#00FFCC]"> 申请DEMO</span>
          </h1>
          <p className="text-white/40 text-base max-w-2xl leading-relaxed">
            无论您是寻求商务合作、技术咨询，还是希望亲身体验META66元宇宙生态，我们的团队随时准备为您服务
          </p>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-[1400px] mx-auto px-4 md:px-10 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          {/* Left: Contact Info */}
          <div className="lg:col-span-1 flex flex-col gap-6">
            {/* Contact Cards */}
            <div className="flex flex-col gap-4">
              {contactInfo.map((info) => (
                <div key={info.label} className="flex items-start gap-4 p-5 rounded-xl bg-white/3 border border-white/8 hover:border-[#00FF88]/30 transition-all duration-300">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#00FF88]/10 border border-[#00FF88]/20 flex-shrink-0">
                    <i className={`${info.icon} text-[#00FF88] text-base`} />
                  </div>
                  <div>
                    <p className="text-white/40 text-xs mb-1">{info.label}</p>
                    <p className="text-white/90 text-sm font-semibold">{info.value}</p>
                    <p className="text-white/30 text-xs mt-0.5">{info.sub}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Demo Features */}
            <div className="p-6 rounded-2xl bg-[#00FF88]/5 border border-[#00FF88]/20">
              <h3 className="text-white font-bold text-sm mb-4 flex items-center gap-2">
                <i className="ri-glasses-line text-[#00FF88]" />
                DEMO体验内容
              </h3>
              <div className="flex flex-col gap-3">
                {demoFeatures.map((f) => (
                  <div key={f.title} className="flex items-start gap-3">
                    <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#00FF88]/10 flex-shrink-0">
                      <i className={`${f.icon} text-[#00FF88] text-xs`} />
                    </div>
                    <div>
                      <p className="text-white/80 text-xs font-semibold">{f.title}</p>
                      <p className="text-white/40 text-xs leading-relaxed">{f.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Office Image */}
            <div className="rounded-2xl overflow-hidden border border-white/8 h-48">
              <img
                src="https://readdy.ai/api/search-image?query=futuristic%20metaverse%20office%20space%20green%20neon%20holographic%20displays%20dark%20interior%20sci-fi%20technology%20cinematic&width=600&height=400&seq=office-img&orientation=landscape"
                alt="META66总部"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          {/* Right: Form */}
          <div className="lg:col-span-2">
            <div className="rounded-2xl border border-white/8 bg-white/2 overflow-hidden">
              {/* Tab Switcher */}
              <div className="flex border-b border-white/8 p-1 gap-1 bg-white/3">
                {([
                  { id: 'demo', label: '申请DEMO体验', icon: 'ri-glasses-line' },
                  { id: 'contact', label: '商务合作咨询', icon: 'ri-shake-hands-line' },
                ] as { id: FormTab; label: string; icon: string }[]).map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => { setActiveTab(tab.id); setSubmitted(false); }}
                    className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'bg-[#00FF88] text-black'
                        : 'text-white/40 hover:text-white/70'
                    }`}
                  >
                    <i className={tab.icon} />
                    <span>{tab.label}</span>
                  </button>
                ))}
              </div>

              <div className="p-6 md:p-8">
                {submitted ? (
                  <div className="flex flex-col items-center justify-center py-16 gap-6">
                    <div className="w-20 h-20 flex items-center justify-center rounded-full bg-[#00FF88]/20 border-2 border-[#00FF88]" style={{ boxShadow: '0 0 30px rgba(0,255,136,0.3)' }}>
                      <i className="ri-check-line text-[#00FF88] text-4xl" />
                    </div>
                    <div className="text-center">
                      <h3 className="text-white font-bold text-xl mb-2">
                        {activeTab === 'demo' ? 'DEMO申请已提交！' : '消息已发送！'}
                      </h3>
                      <p className="text-white/40 text-sm leading-relaxed max-w-sm">
                        {activeTab === 'demo'
                          ? '我们的体验顾问将在24小时内与您联系，为您安排专属DEMO体验时间'
                          : '感谢您的咨询，我们的商务团队将在1个工作日内回复您'}
                      </p>
                    </div>
                    <button
                      onClick={() => setSubmitted(false)}
                      className="px-6 py-3 rounded-full border border-[#00FF88]/30 text-[#00FF88] text-sm font-semibold hover:bg-[#00FF88]/10 transition-all duration-300 cursor-pointer whitespace-nowrap"
                    >
                      再次提交
                    </button>
                  </div>
                ) : (
                  <form data-readdy-form id={`form-${activeTab}`} onSubmit={handleSubmit} className="flex flex-col gap-5">
                    {activeTab === 'demo' ? (
                      <>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                          <div className="flex flex-col gap-2">
                            <label className="text-white/50 text-xs font-semibold">姓名 *</label>
                            <input name="name" type="text" required placeholder="您的姓名" className="px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm placeholder-white/20 focus:outline-none focus:border-[#00FF88]/50 transition-colors" />
                          </div>
                          <div className="flex flex-col gap-2">
                            <label className="text-white/50 text-xs font-semibold">手机号 *</label>
                            <input name="phone" type="tel" required placeholder="您的手机号" className="px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm placeholder-white/20 focus:outline-none focus:border-[#00FF88]/50 transition-colors" />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                          <div className="flex flex-col gap-2">
                            <label className="text-white/50 text-xs font-semibold">邮箱</label>
                            <input name="email" type="email" placeholder="您的邮箱地址" className="px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm placeholder-white/20 focus:outline-none focus:border-[#00FF88]/50 transition-colors" />
                          </div>
                          <div className="flex flex-col gap-2">
                            <label className="text-white/50 text-xs font-semibold">公司/机构</label>
                            <input name="company" type="text" placeholder="您所在的公司或机构" className="px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm placeholder-white/20 focus:outline-none focus:border-[#00FF88]/50 transition-colors" />
                          </div>
                        </div>
                        <div className="flex flex-col gap-2">
                          <label className="text-white/50 text-xs font-semibold">您最感兴趣的体验方向</label>
                          <select name="interest" className="px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-[#00FF88]/50 transition-colors cursor-pointer">
                            <option value="" className="bg-[#020810]">请选择</option>
                            <option value="XR沉浸体验" className="bg-[#020810]">XR沉浸体验（VR/AR/MR/全息）</option>
                            <option value="6G网络演示" className="bg-[#020810]">6G超连接网络演示</option>
                            <option value="数字孪生" className="bg-[#020810]">数字孪生城市系统</option>
                            <option value="脑机接口" className="bg-[#020810]">脑机接口体验</option>
                            <option value="Web5.0协议" className="bg-[#020810]">Web5.0去中心化协议</option>
                            <option value="量子加密" className="bg-[#020810]">量子加密安全演示</option>
                            <option value="全部体验" className="bg-[#020810]">全部体验（完整DEMO）</option>
                          </select>
                        </div>
                        <div className="flex flex-col gap-2">
                          <label className="text-white/50 text-xs font-semibold">期望体验时间</label>
                          <input name="preferred_time" type="text" placeholder="例如：2026年4月中旬，工作日下午" className="px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm placeholder-white/20 focus:outline-none focus:border-[#00FF88]/50 transition-colors" />
                        </div>
                        <div className="flex flex-col gap-2">
                          <label className="text-white/50 text-xs font-semibold">
                            补充说明
                            <span className="text-white/20 ml-2 font-normal">{charCount}/500</span>
                          </label>
                          <textarea
                            name="message"
                            rows={4}
                            maxLength={500}
                            placeholder="请描述您的具体需求或期望了解的内容..."
                            onChange={(e) => setCharCount(e.target.value.length)}
                            className="px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm placeholder-white/20 focus:outline-none focus:border-[#00FF88]/50 transition-colors resize-none"
                          />
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                          <div className="flex flex-col gap-2">
                            <label className="text-white/50 text-xs font-semibold">姓名 *</label>
                            <input name="name" type="text" required placeholder="您的姓名" className="px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm placeholder-white/20 focus:outline-none focus:border-[#00FF88]/50 transition-colors" />
                          </div>
                          <div className="flex flex-col gap-2">
                            <label className="text-white/50 text-xs font-semibold">邮箱 *</label>
                            <input name="email" type="email" required placeholder="您的邮箱地址" className="px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm placeholder-white/20 focus:outline-none focus:border-[#00FF88]/50 transition-colors" />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                          <div className="flex flex-col gap-2">
                            <label className="text-white/50 text-xs font-semibold">手机号</label>
                            <input name="phone" type="tel" placeholder="您的手机号" className="px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm placeholder-white/20 focus:outline-none focus:border-[#00FF88]/50 transition-colors" />
                          </div>
                          <div className="flex flex-col gap-2">
                            <label className="text-white/50 text-xs font-semibold">公司/机构 *</label>
                            <input name="company" type="text" required placeholder="您所在的公司或机构" className="px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm placeholder-white/20 focus:outline-none focus:border-[#00FF88]/50 transition-colors" />
                          </div>
                        </div>
                        <div className="flex flex-col gap-2">
                          <label className="text-white/50 text-xs font-semibold">合作类型</label>
                          <select name="cooperation_type" className="px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-[#00FF88]/50 transition-colors cursor-pointer">
                            <option value="" className="bg-[#020810]">请选择合作类型</option>
                            <option value="战略合作" className="bg-[#020810]">战略合作伙伴</option>
                            <option value="技术合作" className="bg-[#020810]">技术研发合作</option>
                            <option value="投资合作" className="bg-[#020810]">投资/融资合作</option>
                            <option value="媒体合作" className="bg-[#020810]">媒体/内容合作</option>
                            <option value="政府合作" className="bg-[#020810]">政府/机构合作</option>
                            <option value="其他" className="bg-[#020810]">其他合作</option>
                          </select>
                        </div>
                        <div className="flex flex-col gap-2">
                          <label className="text-white/50 text-xs font-semibold">
                            合作意向描述 *
                            <span className="text-white/20 ml-2 font-normal">{charCount}/500</span>
                          </label>
                          <textarea
                            name="message"
                            rows={5}
                            required
                            maxLength={500}
                            placeholder="请详细描述您的合作意向、背景及期望达成的目标..."
                            onChange={(e) => setCharCount(e.target.value.length)}
                            className="px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm placeholder-white/20 focus:outline-none focus:border-[#00FF88]/50 transition-colors resize-none"
                          />
                        </div>
                      </>
                    )}

                    <button
                      type="submit"
                      disabled={submitting}
                      className="flex items-center justify-center gap-3 w-full py-4 rounded-xl bg-[#00FF88] text-black font-bold text-base hover:bg-[#00FFCC] transition-all duration-300 disabled:opacity-60 cursor-pointer whitespace-nowrap"
                      style={{ boxShadow: '0 0 20px rgba(0,255,136,0.3)' }}
                    >
                      {submitting ? (
                        <><i className="ri-loader-4-line animate-spin" /><span>提交中...</span></>
                      ) : (
                        <><i className={activeTab === 'demo' ? 'ri-glasses-line' : 'ri-send-plane-line'} /><span>{activeTab === 'demo' ? '立即申请DEMO体验' : '发送合作咨询'}</span></>
                      )}
                    </button>

                    <p className="text-white/20 text-xs text-center">提交即表示您同意META66的隐私政策和服务条款</p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
